import React from "react";
import { Col, Row } from "react-bootstrap";
import { object, array } from "prop-types";
import NumberedScoreScale from "components/DynamicScoreReportScales/NumberedScoreScale";
import { scaleTraitFieldToPretty } from "components/DynamicScoreReportScales/constants";
import WorkHabitsIcon from "resources/images/dynamic_score_reports/risk/score-report/work-habits-icon.png";
import IntegrityIcon from "resources/images/dynamic_score_reports/risk/score-report/integrity-icon.png";

const WPP = (props) => {
  // ---------------------------- Prop Deconstruction ---------------------------
  const {
    reportText,
    SelectedScoreRangeSection,
    SubcategoryScoreColumns,
    testScores,
  } = props;

  // ---------------------------- Results Details ----------------------------
  const getResultsDetails = () => (
    <div className="gray-bottom-border pb-4 pt-3">
      <h3 className="reportHeader pb-1">Results Details</h3>
      <p
        className="mb-0"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: reportText.get("overall_text"),
        }}
      />
    </div>
  );

  // ---------------------------- Score Details ----------------------------
  const getScoreDetails = () => {
    if (!testScores) {
      return null;
    }

    const tableStructure = {
      "Work Habits": {
        imgSrc: WorkHabitsIcon,
        traitArr: ["CON_percentile", "PER_percentile"],
      },
      Integrity: {
        imgSrc: IntegrityIcon,
        traitArr: ["HON_percentile", "THEFT_percentile"],
      },
    };

    const rows = Object.entries(tableStructure).map(
      ([title, { imgSrc, traitArr }]) => (
        <tr key={title}>
          <td>
            <img
              alt={title}
              src={imgSrc}
              style={{ height: "88px", width: "88px" }}
            />
          </td>
          <td
            className="font-weight-bold"
            style={{ fontSize: "16px", color: "#19355f" }}
          >
            {title}
          </td>
          <td className="pt-4 pb-2">
            {traitArr.map((trait) => (
              <React.Fragment key={trait}>
                {getScoreScaleFromTrait(trait)}
              </React.Fragment>
            ))}
          </td>
        </tr>
      )
    );

    return (
      <div className="gray-bottom-border">
        <h3 className="reportHeader mt-3">Score Details</h3>
        <table className="score-details-table w-100">
          <colgroup>
            <col span="1" style={{ width: "10%" }} />
            <col span="1" style={{ width: "10%" }} />
            <col span="1" style={{ width: "100%" }} />
          </colgroup>
          <tbody>{rows}</tbody>
        </table>
        {SelectedScoreRangeSection && (
          <div className="mx-3 mb-3">
            <hr className="divider" />
            {SelectedScoreRangeSection}
          </div>
        )}
      </div>
    );
  };

  const getScoreScaleFromTrait = (trait) => {
    const score = testScores.get(trait);
    const scaleProps = {
      score,
      maxScore: 100,
      cellSpan: 5,
      cellStyle: "wpp",
    };

    return (
      <li className="d-flex mb-4 mt-1">
        <Row className="w-100">
          <Col xs={12} sm={2} md={2} lg={2} className="text-right">
            {scaleTraitFieldToPretty[31][trait]}
          </Col>
          <Col xs={12} sm={10} md={10} lg={10} className="score-bars">
            <NumberedScoreScale {...scaleProps} />
          </Col>
        </Row>
      </li>
    );
  };

  // ---------------------------- Validity and Response Style ----------------------------
  const getValidityAndResponseStyle = () => (
    <div className="gray-bottom-border">
      <h3 className="reportHeader my-3">Validity and Response Style</h3>
      <p>
        {`The WPP contains an internal validity scale known as the
           Self-Enhancing Scale which is designed to detect and, where
           applicable, correct for instances in which individuals exhibit
           tendencies to "fake" the test, or present themselves in an overly
           positive light.`}
      </p>
      {SubcategoryScoreColumns}
    </div>
  );

  // ---------------------------- Score Explanation ----------------------------
  const getScoreExplanation = () => {
    const firstColExplanations = [];
    const secondColExplanations = [];

    Object.entries(scaleTraitFieldToPretty[31]).forEach(
      ([trait, traitName]) => {
        const traitAbbrev = trait.split("_")[0];
        const traitKey = `${traitAbbrev}_text`;
        if (!reportText.get(traitKey)) {
          return;
        }

        const explanation = (
          <Row key={traitAbbrev} className="mb-4 mr-2">
            <Col>
              <Row className="mb-2">
                <div className="score-explanation trait-header">
                  <span style={{ fontSize: "16px" }}>
                    {traitAbbrev === "HON" ? "Honesty/Integrity" : traitName}
                  </span>
                  <span style={{ fontSize: "20px" }}>
                    {testScores.get(trait)}
                  </span>
                </div>
                {getScoreExplanationIcon(traitName)}
              </Row>
              <div
                key={traitAbbrev}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: reportText.get(traitKey),
                }}
              />
            </Col>
          </Row>
        );

        if (firstColExplanations.length < 2) {
          firstColExplanations.push(explanation);
        } else {
          secondColExplanations.push(explanation);
        }
      }
    );

    return (
      <div className="gray-bottom-border">
        <h3 className="reportHeader my-3">Score Explanation</h3>
        <Row>
          <Col lg={6}>{firstColExplanations}</Col>
          <Col lg={6}>{secondColExplanations}</Col>
        </Row>
      </div>
    );
  };

  const getScoreExplanationIcon = (traitName) => {
    try {
      const parsedName = (traitName || "")
        .replaceAll(/[\s/]/g, "-")
        .toLowerCase();
      // eslint-disable-next-line global-require
      const imgSrc = require(
        `resources/images/dynamic_score_reports/risk/score-report/${parsedName}-icon.png`
      );

      return <img src={imgSrc} alt={traitName} width="64" height="64" />;
    } catch (error) {
      return "";
    }
  };

  return reportText ? (
    <div>
      {getResultsDetails()}
      {getScoreDetails()}
      {getValidityAndResponseStyle()}
      {getScoreExplanation()}
    </div>
  ) : null;
};

WPP.propTypes = {
  reportText: object,
  SelectedScoreRangeSection: object,
  SubcategoryScoreColumns: array,
  testScores: object,
};

export default WPP;
